// @flow strict
import * as React from 'react';
import { findIndex, propEq, sort } from 'ramda';

import {
  getStateAldData,
} from '../state-ald-data.js';
import {
  calculateStartingPanel,
  compareSkills,
  getPanelNumber,
  getStrandIndicatorGroups,
} from './skills-page.helpers.js';
import { Partner } from '../partner';
import type { AchievementLevel, Grade, Strand } from '../ald-types'

export type StrandExpandedState = { [string]: 'expanded' | 'collapsed' };
type NextDataSelection = {|
  partnerId: ?string,
  gradeId: ?string,
  subjectId: ?string,
|};

type State = {
  aldData: Grade[],
  setAldData: (Grade[]) => void,
  skills: AchievementLevel[],
  setSkills: (AchievementLevel[]) => void,
  strandIndicatorGroups: Array<[Strand, string[]]>,
  setStrandIndicatorGroups: (Array<[Strand, string[]]>) => void,
  firstPanel: number,
  setFirstPanel: (number) => void,
  refreshEnabled: boolean,
  setRefreshEnabled: (boolean) => void,
  strandDisplayStatus: StrandExpandedState,
  setStrandDisplayStatus: (StrandExpandedState) => void,
  nextDataSelection: NextDataSelection,
  setNextDataSelection: (NextDataSelection) => void,
};

export const useState = (
  partner: Partner,
  gradeId: string,
  subjectId: string
): State => {
  // eslint-disable-next-line no-undef
  const [aldData, setAldData]:[Grade[], ((Grade[] => Grade[]) | Grade[]) => void] = React.useState([]);
  const [skills, setSkills]:[AchievementLevel[], ((AchievementLevel[] => AchievementLevel[]) | AchievementLevel[]) => void] = React.useState([]);
  const [
    strandIndicatorGroups,
    setStrandIndicatorGroups,
  ]:[Array<[Strand, string[]]>, ((Array<[Strand, string[]]> => Array<[Strand, string[]]>) | Array<[Strand, string[]]>) => void] = React.useState([]);
  // TODO: probably worth saving 'firstPanel' state in browser history so browser back then browser forward retains position
  // firstPanel is the first of two showing panels - range: [0, skills.length-2]
  const [firstPanel, setFirstPanel]:[number, ((number => number) | number) => void] = React.useState(0);
  const [refreshEnabled, setRefreshEnabled]:[boolean, ((boolean => boolean) | boolean) => void] = React.useState(
    !!partner && !!gradeId && !!subjectId
  );
  const [strandDisplayStatus, setStrandDisplayStatus]:[StrandExpandedState, ((StrandExpandedState => StrandExpandedState) | StrandExpandedState) => void] =
    React.useState({
      Algebra: 'collapsed',
      Comprehension: 'collapsed',
      Data: 'collapsed',
      Geometry: 'collapsed',
      'No strand': 'collapsed',
      Number: 'collapsed',
      Reading: 'collapsed',
      Vocabulary: 'collapsed',
      'Writing Modes': 'collapsed',
      'Writing Process': 'collapsed',
      Writing: 'collapsed',
    });
  const [nextDataSelection, setNextDataSelection]:[NextDataSelection, ((NextDataSelection => NextDataSelection) | NextDataSelection) => void] =
    React.useState({
      partnerId: null,
      gradeId: null,
      subjectId: null,
    });

  return {
    aldData,
    setAldData,
    skills,
    setSkills,
    strandIndicatorGroups,
    setStrandIndicatorGroups,
    firstPanel,
    setFirstPanel,
    refreshEnabled,
    setRefreshEnabled,
    strandDisplayStatus,
    setStrandDisplayStatus,
    nextDataSelection,
    setNextDataSelection,
  };
};

export const useEffect = (
  partner: Partner,
  gradeId: string,
  subjectId: string,
  achievementLevel: ?string,
  state: State
) => {
  React.useEffect(() => {
    getStateAldData(partner)
      .then((aldData) => {
        state.setAldData(aldData);
      })
      .catch((e) => console.error(e)); // TODO: Error page, see https://nwea.tpondemand.com/entity/443539-ald-error-page
  }, [partner]);
  React.useEffect(() => {
    const aldData = state.aldData;
    if (aldData.length === 0) {
      return;
    }
    const gradeIndex = findIndex(propEq('gradeId', gradeId), aldData);
    if (gradeIndex < 0) {
      // This case isn't an error; we could be here because we're changing to a
      // new partner with a grade that the previous partner's data didn't have.
      // We can just return here and when the new data finishes loading this
      // effect will be run again.
      return;
    }
    const subjectIndex = findIndex(
      propEq('subjectId', subjectId),
      aldData[gradeIndex].availableSubjects
    );
    if (subjectIndex < 0) {
      // This case isn't an error; we could be here because we're changing to a
      // new partner with a subject that the previous partner's data didn't have.
      // We can just return here and when the new data finishes loading this
      // effect will be run again.
      return;
    }
    const skills =
      aldData[gradeIndex].availableSubjects[subjectIndex].achievementLevels;
    const sortedSkills: AchievementLevel[] = sort(compareSkills, skills);
    const selectedLevel = getPanelNumber(achievementLevel, sortedSkills);
    state.setFirstPanel(
      selectedLevel === null
        ? calculateStartingPanel(sortedSkills.length)
        : selectedLevel
    );
    state.setSkills(sortedSkills);
    state.setStrandIndicatorGroups(getStrandIndicatorGroups(sortedSkills));
  }, [state.aldData, gradeId, subjectId]);
};
